import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import {
    EmailTemplateId,
    PlaceholderParser,
    ReplacementValues,
    TokenPrefix,
} from "@sp-crm/core";
import { SearchInput } from "components/ui/search-input";
import { secondaryClasses } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import { produce } from "immer";
import React from "react";
import { EditorAdapter } from "roosterjs-editor-adapter";
import { InsertTemplateIcon, TriangleDownIcon } from "./ribbon-icons";
import { Template } from "./template";

interface TemplateMenuProps {
    templates?: Template[];
    editor: EditorAdapter;
    onTemplateInserted?: (template: Template) => void;
    tokenReplacements?: Record<string, string>;
    onTokenInserted?: (tokenValue: string) => boolean;
    preserveTokens?: TokenPrefix[];
}

interface TemplateMenuState {
    closeMenu: (() => void) | null;
    waitingForTokenReplacements: string[] | null;
    waitingTemplate: Template | null;
}

type TemplateMenuAction =
    | {
          type: "waitForTokens";
          template: Template;
          tokenValues: string[];
          closeMenu: () => void;
      }
    | {
          type: "bodyInserted";
      };

const initialState: TemplateMenuState = {
    closeMenu: null,
    waitingForTokenReplacements: null,
    waitingTemplate: null,
};

const reducer: React.Reducer<TemplateMenuState, TemplateMenuAction> = (
    state,
    action,
): TemplateMenuState => {
    return produce(state, draft => {
        switch (action.type) {
            case "waitForTokens":
                draft.waitingForTokenReplacements = action.tokenValues;
                draft.waitingTemplate = action.template;
                draft.closeMenu = action.closeMenu;
                break;
            case "bodyInserted":
                draft.waitingForTokenReplacements = null;
                draft.waitingTemplate = null;
                draft.closeMenu = null;
                break;
        }
    });
};

const replaceTemplateContent = (
    template: Template,
    tokenReplacements: ReplacementValues,
    preserveTokens: TokenPrefix[],
): Template => {
    console.log("replaceTemplateContent", preserveTokens);
    return produce(template, draft => {
        draft.body = new PlaceholderParser(draft.body, preserveTokens).apply(
            tokenReplacements,
        );
        draft.subject = new PlaceholderParser(draft.subject, preserveTokens).apply(
            tokenReplacements,
        );
    });
};

export const TemplateMenu: React.FC<TemplateMenuProps> = props => {
    const {
        templates,
        editor,
        onTemplateInserted,
        tokenReplacements,
        onTokenInserted,
        preserveTokens,
    } = props;

    const [templateFilter, setTemplateFilter] = React.useState<string>("");

    const [state, dispatch] = React.useReducer(reducer, initialState);

    const insertTemplateContent = React.useCallback(
        (template: Template, closeMenu: () => void) => {
            // FIXME: Rooster on React18 will not render the new content unless this timeout is here.
            setTimeout(() => {
                editor.insertContent(template.body);
                if (onTemplateInserted) {
                    onTemplateInserted(template);
                }
                closeMenu();
                setTemplateFilter("");
                dispatch({ type: "bodyInserted" });
            }, 0);
        },
        [editor, onTemplateInserted, dispatch],
    );

    const handleTemplate = React.useCallback(
        (templateId: EmailTemplateId, closeMenu: () => void) => {
            if (templateId && editor) {
                const template = (templates || []).find(t => t.id === templateId) || null;
                if (template) {
                    if (tokenReplacements && onTokenInserted) {
                        const replaceableTokens = [
                            ...new PlaceholderParser(template.body).keys,
                            ...new PlaceholderParser(template.subject).keys,
                        ].filter(t => onTokenInserted(t));
                        if (
                            replaceableTokens.length > 0 &&
                            replaceableTokens.some(
                                t => typeof tokenReplacements[t] !== "string",
                            )
                        ) {
                            dispatch({
                                type: "waitForTokens",
                                template,
                                tokenValues: replaceableTokens,
                                closeMenu,
                            });
                        } else {
                            insertTemplateContent(
                                replaceTemplateContent(
                                    template,
                                    tokenReplacements,
                                    preserveTokens,
                                ),
                                closeMenu,
                            );
                        }
                    } else {
                        insertTemplateContent(template, closeMenu);
                    }
                }
            }
        },
        [
            editor,
            templates,
            tokenReplacements,
            insertTemplateContent,
            dispatch,
            onTokenInserted,
            preserveTokens,
        ],
    );

    React.useEffect(() => {
        if (
            state.waitingForTokenReplacements?.length > 0 &&
            state.waitingForTokenReplacements.every(
                t => typeof tokenReplacements[t] === "string",
            ) &&
            state.waitingTemplate &&
            state.closeMenu
        ) {
            insertTemplateContent(
                replaceTemplateContent(
                    state.waitingTemplate,
                    tokenReplacements,
                    preserveTokens,
                ),
                state.closeMenu,
            );
        }
    }, [tokenReplacements, preserveTokens]); // eslint-disable-line react-hooks/exhaustive-deps

    const filteredTemplates = React.useMemo(() => {
        return templates?.filter(t =>
            t.description.toLowerCase().includes(templateFilter.toLowerCase()),
        );
    }, [templates, templateFilter]);

    if (templates && templates.length > 0) {
        return (
            <Menu as="div" className="relative inline-block text-left">
                <MenuButton className={secondaryClasses}>
                    <div className="flex items-center space-x-2">
                        <InsertTemplateIcon className="w-5 h-5" />
                        <TriangleDownIcon className="w-1.5 h-1.5" />
                    </div>
                </MenuButton>
                <MenuItems
                    anchor="bottom start"
                    className="w-56 rounded-md shadow-lg bg-white ring-1 ring-gray-200 ring-opacity-5 divide-y divide-gray-100 focus:outline-none [--anchor-gap:0.5rem]">
                    <p className="p-2 text-brand-700">Insert template</p>
                    {templates.length > 5 ? (
                        <SearchInput
                            value={templateFilter}
                            placeholder="Filter templates"
                            onChange={e => setTemplateFilter(e.target.value)}
                        />
                    ) : null}
                    {filteredTemplates.map(t => (
                        <MenuItem key={t.id}>
                            {({ focus, close }) => (
                                <div
                                    className={`${
                                        focus
                                            ? "bg-brand-100 text-brand-900"
                                            : "text-gray-700"
                                    } group flex items-center px-4 py-2 text-sm rounded-md`}
                                    onClick={() => handleTemplate(t.id, close)}>
                                    {t.description}
                                </div>
                            )}
                        </MenuItem>
                    ))}
                    {state.waitingForTokenReplacements ? (
                        <div className="absolute inset-0 background-gray-500 opacity-25 flex items-center justify-center">
                            <Spinner />
                        </div>
                    ) : null}
                </MenuItems>
            </Menu>
        );
    }

    return null;
};
